import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizableHandleOptions {
  initialWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Callback when width changes (called on drag end) */
  onWidthChange: ((width: number) => void) | undefined;
  enabled?: boolean;
}

interface UseResizableHandleReturn {
  /** Current committed width */
  currentWidth: number;
  isDragging: boolean;
  handleRef: React.RefObject<HTMLDivElement | null>;
  containerProps: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
  };
}

/**
 * Hook for creating a performant resizable handle with smooth drag interactions.
 *
 * Performance optimizations:
 * - Uses CSS transforms during drag (no React re-renders)
 * - Updates React state only once on drag end
 * - Uses pointer events for better device support
 * - Proper cleanup to prevent memory leaks
 *
 * @example
 * ```tsx
 * const { currentWidth, isDragging, handleRef, containerProps } = useResizableHandle({
 *   initialWidth: 200,
 *   minWidth: 120,
 *   maxWidth: 600,
 *   onWidthChange: (width) => setRowHeaderWidth(width),
 * });
 *
 * return (
 *   <div {...containerProps}>
 *     <div ref={handleRef} data-dragging={isDragging} />
 *   </div>
 * );
 * ```
 */
export function useResizableHandle({
  initialWidth,
  minWidth,
  maxWidth,
  onWidthChange,
  enabled = true,
}: UseResizableHandleOptions): UseResizableHandleReturn {
  const [currentWidth, setCurrentWidth] = useState(initialWidth);
  const [isDragging, setIsDragging] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const previousWidthRef = useRef(currentWidth);
  const isDraggingRef = useRef(false);
  const currentWidthRef = useRef(initialWidth);
  const captureTargetRef = useRef<HTMLDivElement | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const nextWidthRef = useRef<number | null>(null);

  const dragStateRef = useRef({
    startX: 0,
    startWidth: 0,
    pointerId: -1,
  });

  useEffect(() => {
    currentWidthRef.current = currentWidth;
  }, [currentWidth]);

  useEffect(() => {
    if (isDraggingRef.current) return;
    if (initialWidth !== currentWidthRef.current) {
      setCurrentWidth(initialWidth);
      previousWidthRef.current = initialWidth;
      currentWidthRef.current = initialWidth;
    }
  }, [initialWidth]);

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, Math.round(value))),
    [minWidth, maxWidth],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;

      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);

      dragStateRef.current = {
        startX: e.clientX,
        startWidth: currentWidthRef.current,
        pointerId: e.pointerId,
      };

      captureTargetRef.current = e.currentTarget;

      if (e.currentTarget.setPointerCapture) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [enabled],
  );

  const handleWindowPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDraggingRef.current || !handleRef.current) return;
      if (e.pointerId !== dragStateRef.current.pointerId) return;

      const deltaX = e.clientX - dragStateRef.current.startX;
      const newWidth = clamp(dragStateRef.current.startWidth + deltaX);
      nextWidthRef.current = newWidth;

      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (!handleRef.current || nextWidthRef.current === null) return;
        const offsetX = nextWidthRef.current - dragStateRef.current.startWidth;
        handleRef.current.style.transform = `translateX(${offsetX}px)`;
      });
    },
    [clamp],
  );

  const handleWindowPointerUpOrCancel = useCallback(
    (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      if (e.pointerId !== dragStateRef.current.pointerId) return;

      const deltaX = e.clientX - dragStateRef.current.startX;
      const finalWidth = clamp(dragStateRef.current.startWidth + deltaX);

      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      nextWidthRef.current = null;

      if (handleRef.current) {
        handleRef.current.style.transform = '';
      }

      previousWidthRef.current = finalWidth;
      currentWidthRef.current = finalWidth;
      setCurrentWidth(finalWidth);
      isDraggingRef.current = false;
      setIsDragging(false);

      if (onWidthChange) {
        onWidthChange(finalWidth);
      }

      try {
        if (captureTargetRef.current?.releasePointerCapture) {
          captureTargetRef.current.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Pointer capture already released
      }

      captureTargetRef.current = null;
    },
    [clamp, onWidthChange],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => handleWindowPointerMove(e);
    const handlePointerUp = (e: PointerEvent) => handleWindowPointerUpOrCancel(e);
    const handlePointerCancel = (e: PointerEvent) => handleWindowPointerUpOrCancel(e);

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [isDragging, handleWindowPointerMove, handleWindowPointerUpOrCancel]);

  return {
    currentWidth,
    isDragging,
    handleRef,
    containerProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: (e) => handleWindowPointerMove(e.nativeEvent),
      onPointerUp: (e) => handleWindowPointerUpOrCancel(e.nativeEvent),
      onPointerCancel: (e) => handleWindowPointerUpOrCancel(e.nativeEvent),
    },
  };
}
